// client/src/pages/SectorManager/Dashboard/index.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState, useAuth } from '../../../context/AuthContext';
import { getUserProfile } from '../../../api/userApi';
import { toast } from 'react-toastify';
import customFetch from '../../../api/customFetch';
import {
  DashboardContainer,
  Header,
  HeaderContent,
  UserSection,
  Avatar,
  UserInfo,
  Greeting,
  WelcomeText,
  RoleBadge,
  SectorTags,
  SectorTag,
  HeaderActions,
  Button,
  StatsContainer,
  StatCard,
  StatNumber,
  StatLabel,
  ProgressSection,
  ProgressRing,
  RingText,
  RingNumber,
  RingLabel,
  ProgressDetails,
  Tabs,
  Tab,
  Section,
  SectionTitle,
  SectionHeader,
  EmployeeGrid,
  EmployeeCard,
  EmployeeHeader,
  EmployeeAvatar,
  EmployeeInfo,
  EmployeeName,
  EmployeeEmail,
  StatusBadge,
  ProgressBar,
  ProgressFill,
  ProgressText,
  CourseGrid,
  CourseCard,
  CourseTitle,
  CourseDescription,
  CourseMeta,
  MetaItem,
  ActionButtons,
  TableContainer,
  Table,
  FilterBar,
  Select,
  SearchInput,
  StatusCardsGrid,
  StatusCard,
  StatusCardHeader,
  StatusCardTitle,
  StatusCardNumber,
  StatusCardList,
  StatusCardItem,
  StatusCardMore,
  ModalOverlay,
  Modal,
  ModalClose,
  ModalHeader,
  ModalAvatar,
  ModalTitle,
  ModalSubtitle,
  ModalStats,
  ModalStat,
  ModalStatValue,
  ModalStatLabel,
  ModalSection,
  ModalSectionTitle,
  ModalCourseList,
  ModalCourse,
  ModalCourseHeader,
  ModalCourseTitle,
  ModalCourseProgress,
  Score,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyText,
  LoadingContainer,
  Spinner,
  LoadingText,
  Alert,
  AlertClose,
  Checkbox,
  ActionMenu,
} from './styles';

import ClimateSurveyChartCard from '../../../components/survey/ClimateSurveyChartCard';
import CoursesCompletionTable from '../../../components/CoursesCompletionTable/CoursesCompletionTable';

const SectorManagerDashboard = () => {
  // State - נתוני מנהל
  const [managerCourses, setManagerCourses] = useState([]);
  const [managerExams, setManagerExams] = useState([]);
  const [managerStats, setManagerStats] = useState({
    courseStats: {
      total: 0,
      completed: 0,
      inProgress: 0,
      pending: 0,
      completionRate: 0,
    },
    examStats: { total: 0, completed: 0, failed: 0, pending: 0, passRate: 0 },
  });

  // State - נתוני עובדים
  const [employees, setEmployees] = useState([]);
  const [employeesBySector, setEmployeesBySector] = useState({});
  const [sectorStats, setSectorStats] = useState([]);
  const [viewableSectors, setViewableSectors] = useState([]);
  const [reminderToast, setReminderToast] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);

  // State - UI
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedSector, setSelectedSector] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');

  // State - Modal
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // State - Alerts
  const [alert, setAlert] = useState(null);

  const { isLoggedIn, user, loading: authLoading } = useAuthState();
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!isLoggedIn) {
      navigate('/user/login');
      return;
    }
    fetchAllData();
  }, [navigate, isLoggedIn, authLoading]);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      // טעינת נתוני המנהל עצמו
      const profileResponse = await getUserProfile();
      if (profileResponse.success) {
        setManagerCourses(profileResponse.user.courses || []);
        setManagerExams(profileResponse.user.exams || []);
        setManagerStats({
          courseStats:
            profileResponse.user.courseStats || managerStats.courseStats,
          examStats: profileResponse.user.examStats || managerStats.examStats,
        });
      }

      // טעינת עובדים
      const { data: employeesData } = await customFetch.get(
        '/users/my-employees',
      );
      setEmployees(employeesData.employees || []);
      setEmployeesBySector(employeesData.employeesBySector || {});
      setViewableSectors(employeesData.viewableSectors || []);

      // טעינת סטטיסטיקות סקטורים
      const { data: statsData } = await customFetch.get('/users/sector-stats');
      setSectorStats(statsData.sectorStats || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        await logout();
        navigate('/user/login');
      } else {
        showAlert('error', 'שגיאה בטעינת הנתונים');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeDetails = async (employeeId) => {
    try {
      setLoadingDetails(true);
      const { data } = await customFetch.get(`/users/employee/${employeeId}`);
      setEmployeeDetails(data.employee);
      setSelectedEmployee(employeeId);
    } catch (error) {
      showAlert('error', 'שגיאה בטעינת פרטי העובד');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/user/login');
  };

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'בוקר טוב';
    if (hour < 17) return 'צהריים טובים';
    if (hour < 21) return 'ערב טוב';
    return 'לילה טוב';
  };

  const getStatusText = (status) => {
    const map = {
      pending: 'ממתין',
      in_progress: 'בתהליך',
      completed: 'הושלם',
      failed: 'נכשל',
    };
    return map[status] || status;
  };

  // חישובים
  const totalStats = {
    totalEmployees: employees.length,
    completedAll: employees.filter((e) => e.overallProgress === 100).length,
    avgProgress: employees.length
      ? Math.round(
          employees.reduce((sum, e) => sum + (e.overallProgress || 0), 0) /
            employees.length,
        )
      : 0,
    activeThisWeek: employees.filter((e) => {
      if (!e.lastLogin) return false;
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      return new Date(e.lastLogin) > weekAgo;
    }).length,
    notStarted: employees.filter((e) => e.overallProgress === 0).length,
  };

  const employeesByStatus = {
    notStarted: employees.filter((e) => e.overallProgress === 0),
    inProgress: employees.filter(
      (e) => e.overallProgress > 0 && e.overallProgress < 100,
    ),
    completed: employees.filter((e) => e.overallProgress === 100),
  };

  // פילטור עובדים
  const filteredEmployees = employees.filter((emp) => {
    const matchesSector = selectedSector === 'all' || emp.sector === selectedSector;
    const matchesSearch =
      !searchTerm ||
      emp.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSector && matchesSearch;
  });

  // טיפול בבחירת עובדים
  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId],
    );
  };

  const selectAllFiltered = () => {
    const allIds = filteredEmployees.map((e) => e._id);
    setSelectedEmployees((prev) =>
      prev.length === allIds.length ? [] : allIds,
    );
  };

  const selectByStatus = (status) => {
    const ids = employeesByStatus[status].map((e) => e._id);
    setSelectedEmployees(ids);
  };

  // שליחת מייל
  const handleSendEmail = async () => {
    if (!selectedEmployees.length) {
      showAlert('warning', 'בחר לפחות עובד אחד');
      return;
    }
    if (!emailSubject.trim() || !emailMessage.trim()) {
      showAlert('warning', 'נא למלא נושא והודעה');
      return;
    }

    setSendingReminders(true);
    setReminderToast(null);

    try {
      // קריאה ל-API של תזכורות (לא bulk-email)
      const response = await customFetch.post('/reminders/send-overdue', {});

      if (response.data.success) {
        setReminderToast({
          type: 'success',
          message: `✅ נשלחו ${response.data.emailsSent} מיילים בהצלחה`,
        });

        // סגור את המודאל אחרי שליחה מוצלחת
        setShowEmailModal(false);
        setEmailSubject('');
        setEmailMessage('');
        setSelectedEmployees([]);
        toast.success(`נשלחו ${response.data.emailsSent} מיילים בהצלחה`);
      } else {
        setReminderToast({
          type: 'error',
          message: response.data.message || 'שגיאה בשליחת התזכורות',
        });
      }
    } catch (error) {
      console.error('Error sending reminders:', error);
      setReminderToast({
        type: 'error',
        message: 'שגיאה בשליחת התזכורות',
      });
    } finally {
      setSendingReminders(false);
      // הסר את הטוסט אחרי 5 שניות
      setTimeout(() => setReminderToast(null), 5000);
    }
  };

  const handleSendReminders = async () => {
    // בדיקה אם יש עובדים שצריכים תזכורות
    const totalNeedingReminders =
      employeesByStatus.notStarted.length + employeesByStatus.inProgress.length;

    if (totalNeedingReminders === 0) {
      setReminderToast({
        type: 'info',
        message: 'אין עובדים שצריכים תזכורות',
      });
      return;
    }

    // הודעת אישור
    const confirmed = window.confirm(
      `אתה עומד לשלוח תזכורות ל-${totalNeedingReminders} עובדים. להמשיך?`,
    );

    if (!confirmed) return;

    setSendingReminders(true);
    setReminderToast(null);

    try {
      const response = await customFetch.post(
        '/api/reminders/send-overdue',
        {},
      );

      if (response.data.success) {
        setReminderToast({
          type: 'success',
          message: `✅ נשלחו ${response.data.emailsSent} מיילים בהצלחה`,
        });
      } else {
        setReminderToast({
          type: 'error',
          message: response.data.message || 'שגיאה בשליחת התזכורות',
        });
      }
    } catch (error) {
      console.error('Error sending reminders:', error);
      setReminderToast({
        type: 'error',
        message: 'שגיאה בשליחת התזכורות',
      });
    } finally {
      setSendingReminders(false);
      // הסר את הטוסט אחרי 5 שניות
      setTimeout(() => setReminderToast(null), 5000);
    }
  };

  // שליחת תזכורת
  const sendReminder = async (type) => {
    const targetEmployees =
      type === 'notStarted'
        ? employeesByStatus.notStarted
        : employeesByStatus.inProgress;

    if (!targetEmployees.length) {
      showAlert('info', 'אין עובדים בקטגוריה זו');
      return;
    }

    setSelectedEmployees(targetEmployees.map((e) => e._id));
    setEmailSubject(
      type === 'notStarted'
        ? 'תזכורת: השלמת לומדות חובה'
        : 'תזכורת: המשך לומדות בתהליך',
    );
    setEmailMessage(
      type === 'notStarted'
        ? 'שלום,\n\nזוהי תזכורת להשלמת הלומדות שטרם התחלת.\nנא להיכנס למערכת ולהשלים את הלומדות בהקדם.\n\nבברכה'
        : 'שלום,\n\nזוהי תזכורת להמשיך ולהשלים את הלומדות שהתחלת.\nנא להיכנס למערכת ולסיים את הלומדות.\n\nבברכה',
    );
    setShowEmailModal(true);
  };

  if (authLoading || loading) {
    return (
      <DashboardContainer>
        <LoadingContainer>
          <Spinner />
          <LoadingText>טוען נתונים...</LoadingText>
        </LoadingContainer>
      </DashboardContainer>
    );
  }

  if (!isLoggedIn) return null;

  return (
    <DashboardContainer>
      {/* Alert */}
      {alert && (
        <Alert type={alert.type}>
          {alert.message}
          <AlertClose onClick={() => setAlert(null)}>✕</AlertClose>
        </Alert>
      )}

      {/* Header */}
      <Header>
        <HeaderContent>
          <UserSection>
            <Avatar>{user?.fullName?.charAt(0) || 'מ'}</Avatar>
            <UserInfo>
              <Greeting>{getGreeting()},</Greeting>
              <WelcomeText>{user?.fullName || user?.username}</WelcomeText>
              <RoleBadge>מנהל קטגוריה</RoleBadge>
              <SectorTags>
                {viewableSectors.map((sector) => (
                  <SectorTag key={sector}>{sector}</SectorTag>
                ))}
              </SectorTags>
            </UserInfo>
          </UserSection>
          <HeaderActions>
            <Button
              variant='secondary'
              onClick={() => navigate('/user/dashboard')}
            >
              📚 הלומדות שלי
            </Button>
            <Button variant='danger' onClick={handleLogout}>
              🚪 התנתק
            </Button>
          </HeaderActions>
        </HeaderContent>
      </Header>

      {/* Stats - צוות */}
      <StatsContainer>
        <StatCard color='#667eea'>
          <StatNumber color='#667eea'>{totalStats.totalEmployees}</StatNumber>
          <StatLabel>👥 סה"כ עובדים</StatLabel>
        </StatCard>
        <StatCard color='#27ae60'>
          <StatNumber color='#27ae60'>{totalStats.completedAll}</StatNumber>
          <StatLabel>✅ השלימו הכל</StatLabel>
        </StatCard>
        <StatCard color='#e74c3c'>
          <StatNumber color='#e74c3c'>{totalStats.notStarted}</StatNumber>
          <StatLabel>🚫 טרם התחילו</StatLabel>
        </StatCard>
        <StatCard color='#3498db'>
          <StatNumber color='#3498db'>{totalStats.avgProgress}%</StatNumber>
          <StatLabel>📈 התקדמות ממוצעת</StatLabel>
        </StatCard>
      </StatsContainer>

      {/* Progress Ring - המנהל עצמו */}
      <ProgressSection>
        <ProgressRing>
          <svg viewBox='0 0 100 100'>
            <defs>
              <linearGradient id='gradient' x1='0%' y1='0%' x2='100%' y2='0%'>
                <stop offset='0%' stopColor='#667eea' />
                <stop offset='100%' stopColor='#764ba2' />
              </linearGradient>
            </defs>
            <circle className='ring-bg' cx='50' cy='50' r='40' />
            <circle
              className='ring-fill'
              cx='50'
              cy='50'
              r='40'
              strokeDasharray={`${
                managerStats.courseStats.completionRate * 2.51
              } 251`}
            />
          </svg>
          <RingText>
            <RingNumber>{managerStats.courseStats.completionRate}%</RingNumber>
            <RingLabel>הלומדות שלי</RingLabel>
          </RingText>
        </ProgressRing>
        <ProgressDetails>
          <h3>ההתקדמות האישית שלך</h3>
          <p>
            השלמת {managerStats.courseStats.completed} מתוך{' '}
            {managerStats.courseStats.total} לומדות
          </p>
          <p style={{ marginTop: '8px', color: '#999' }}>
            📈 התקדמות ממוצעת של הצוות: {totalStats.avgProgress}%
          </p>
        </ProgressDetails>
      </ProgressSection>

      {/* Tabs */}
      <Tabs>
        <Tab
          $active={activeTab === 'overview'}
          onClick={() => setActiveTab('overview')}
        >
          📊 סקירת צוות
        </Tab>
        <Tab
          $active={activeTab === 'employees'}
          onClick={() => setActiveTab('employees')}
        >
          👥 עובדים ({employees.length})
        </Tab>
        <Tab
          $active={activeTab === 'my-courses'}
          onClick={() => setActiveTab('my-courses')}
        >
          📚 הלומדות שלי ({managerCourses.length})
        </Tab>
        <Tab
          $active={activeTab === 'my-exams'}
          onClick={() => setActiveTab('my-exams')}
        >
          📝 מבחנים ({managerExams.length})
        </Tab>
      </Tabs>

      {/* Tab: Overview */}
      {activeTab === 'overview' && (
        <>
          {/* Status Cards with Actions */}
          <StatusCardsGrid>
            <StatusCard color='#e74c3c'>
              <StatusCardHeader>
                <div>
                  <StatusCardTitle>🚫 טרם התחילו</StatusCardTitle>
                  <StatusCardNumber>
                    {employeesByStatus.notStarted.length}
                  </StatusCardNumber>
                </div>
                {employeesByStatus.notStarted.length > 0 && (
                  <Button
                    variant='danger'
                    small
                    onClick={() => sendReminder('notStarted')}
                  >
                    📧 שלח תזכורת
                  </Button>
                )}
              </StatusCardHeader>
              <StatusCardList>
                {employeesByStatus.notStarted.slice(0, 5).map((emp) => (
                  <StatusCardItem key={emp._id}>
                    <span>{emp.fullName}</span>
                    <Button small onClick={() => loadEmployeeDetails(emp._id)}>
                      צפה
                    </Button>
                  </StatusCardItem>
                ))}
                {employeesByStatus.notStarted.length > 5 && (
                  <StatusCardMore
                    onClick={() => {
                      setSelectedSector('all');
                      setActiveTab('employees');
                    }}
                  >
                    +{employeesByStatus.notStarted.length - 5} נוספים...
                  </StatusCardMore>
                )}
              </StatusCardList>
            </StatusCard>

            <StatusCard color='#f39c12'>
              <StatusCardHeader>
                <div>
                  <StatusCardTitle>⏳ בתהליך</StatusCardTitle>
                  <StatusCardNumber>
                    {employeesByStatus.inProgress.length}
                  </StatusCardNumber>
                </div>
                {employeesByStatus.inProgress.length > 0 && (
                  <Button
                    variant='warning'
                    small
                    onClick={() => sendReminder('inProgress')}
                  >
                    📧 שלח תזכורת
                  </Button>
                )}
              </StatusCardHeader>
              <StatusCardList>
                {employeesByStatus.inProgress.slice(0, 5).map((emp) => (
                  <StatusCardItem key={emp._id}>
                    <span>{emp.fullName}</span>
                    <span style={{ color: '#f39c12', fontWeight: 600 }}>
                      {emp.overallProgress}%
                    </span>
                  </StatusCardItem>
                ))}
                {employeesByStatus.inProgress.length > 5 && (
                  <StatusCardMore onClick={() => setActiveTab('employees')}>
                    +{employeesByStatus.inProgress.length - 5} נוספים...
                  </StatusCardMore>
                )}
              </StatusCardList>
            </StatusCard>

            <StatusCard color='#27ae60'>
              <StatusCardHeader>
                <div>
                  <StatusCardTitle>✅ השלימו הכל</StatusCardTitle>
                  <StatusCardNumber>
                    {employeesByStatus.completed.length}
                  </StatusCardNumber>
                </div>
              </StatusCardHeader>
              <StatusCardList>
                {employeesByStatus.completed.slice(0, 5).map((emp) => (
                  <StatusCardItem key={emp._id}>
                    <span>{emp.fullName}</span>
                    <StatusBadge status='completed'>הושלם</StatusBadge>
                  </StatusCardItem>
                ))}
                {employeesByStatus.completed.length > 5 && (
                  <StatusCardMore onClick={() => setActiveTab('employees')}>
                    +{employeesByStatus.completed.length - 5} נוספים...
                  </StatusCardMore>
                )}
              </StatusCardList>
            </StatusCard>
          </StatusCardsGrid>

          <ClimateSurveyChartCard />

          <CoursesCompletionTable />

          {/* Sector Stats */}
          <Section>
            <SectionTitle>📊 ביצועים לפי סקטור</SectionTitle>
            {sectorStats.map((stat) => (
              <div
                key={stat.sector}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                  padding: '16px',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  marginBottom: '12px',
                }}
              >
                <div style={{ width: '140px' }}>
                  <strong style={{ color: '#333' }}>{stat.sector}</strong>
                  <div style={{ fontSize: '0.8rem', color: '#666' }}>
                    {stat.totalEmployees} עובדים
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <ProgressBar>
                    <ProgressFill width={stat.averageProgress} />
                  </ProgressBar>
                </div>
                <div
                  style={{
                    minWidth: '50px',
                    fontWeight: 600,
                    color: '#667eea',
                  }}
                >
                  {stat.averageProgress}%
                </div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                  📚 {stat.completedCoursesTotal || 0} | 🟢{' '}
                  {stat.activeLastWeek || 0} פעילים
                </div>
              </div>
            ))}
          </Section>
        </>
      )}

      {/* Tab: Employees */}
      {activeTab === 'employees' && (
        <Section>
          <SectionHeader>
            <SectionTitle>👥 רשימת עובדים</SectionTitle>
            <ActionMenu>
              {selectedEmployees.length > 0 && (
                <Button
                  variant='primary'
                  onClick={() => setShowEmailModal(true)}
                >
                  📧 שלח מייל ({selectedEmployees.length})
                </Button>
              )}
            </ActionMenu>
          </SectionHeader>

          <FilterBar>
            <Select
              value={selectedSector}
              onChange={(e) => setSelectedSector(e.target.value)}
            >
              <option value='all'>כל הסקטורים ({employees.length})</option>
              {viewableSectors.map((sector) => (
                <option key={sector} value={sector}>
                  {sector} ({employeesBySector[sector]?.length || 0})
                </option>
              ))}
            </Select>
            <SearchInput
              placeholder='🔍 חיפוש לפי שם או מייל...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Checkbox>
              <input
                type='checkbox'
                checked={
                  selectedEmployees.length === filteredEmployees.length &&
                  filteredEmployees.length > 0
                }
                onChange={selectAllFiltered}
              />
              בחר הכל
            </Checkbox>
            <Button
              variant='secondary'
              small
              onClick={() => selectByStatus('notStarted')}
            >
              בחר טרם התחילו
            </Button>
            <Button
              variant='secondary'
              small
              onClick={() => selectByStatus('inProgress')}
            >
              בחר בתהליך
            </Button>
          </FilterBar>

          {filteredEmployees.length === 0 ? (
            <EmptyState>
              <EmptyIcon>📭</EmptyIcon>
              <EmptyTitle>לא נמצאו עובדים</EmptyTitle>
              <EmptyText>נסה לשנות את הסינון</EmptyText>
            </EmptyState>
          ) : (
            <EmployeeGrid>
              {filteredEmployees.map((emp) => {
                const status =
                  emp.overallProgress === 100
                    ? 'completed'
                    : emp.overallProgress > 0
                      ? 'in_progress'
                      : 'not_started';
                return (
                  <EmployeeCard key={emp._id} status={status}>
                    <EmployeeHeader>
                      <Checkbox>
                        <input
                          type='checkbox'
                          checked={selectedEmployees.includes(emp._id)}
                          onChange={() => toggleEmployeeSelection(emp._id)}
                        />
                      </Checkbox>
                      <EmployeeAvatar>{emp.fullName?.charAt(0)}</EmployeeAvatar>
                      <EmployeeInfo>
                        <EmployeeName>{emp.fullName}</EmployeeName>
                        <EmployeeEmail>{emp.email}</EmployeeEmail>
                      </EmployeeInfo>
                      <StatusBadge status={status}>
                        {status === 'completed'
                          ? 'הושלם'
                          : status === 'in_progress'
                            ? 'בתהליך'
                            : 'טרם התחיל'}
                      </StatusBadge>
                    </EmployeeHeader>

                    <ProgressBar>
                      <ProgressFill width={emp.overallProgress || 0} />
                    </ProgressBar>
                    <ProgressText>
                      <span>{emp.overallProgress || 0}% התקדמות</span>
                      <span>
                        {emp.completedCoursesCount || 0} קורסים הושלמו
                      </span>
                    </ProgressText>

                    <CourseMeta>
                      <MetaItem>📍 {emp.sector}</MetaItem>
                      <MetaItem>
                        🕐{' '}
                        {emp.lastLogin
                          ? new Date(emp.lastLogin).toLocaleDateString('he-IL')
                          : 'לא נכנס'}
                      </MetaItem>
                    </CourseMeta>

                    <Button
                      variant='secondary'
                      block
                      onClick={() => loadEmployeeDetails(emp._id)}
                    >
                      צפה בפרטים מלאים
                    </Button>
                  </EmployeeCard>
                );
              })}
            </EmployeeGrid>
          )}
        </Section>
      )}

      {/* Tab: My Courses */}
      {activeTab === 'my-courses' && (
        <Section>
          <SectionTitle>📚 הלומדות שלי</SectionTitle>
          {managerCourses.length === 0 ? (
            <EmptyState>
              <EmptyIcon>📭</EmptyIcon>
              <EmptyTitle>אין לומדות</EmptyTitle>
              <EmptyText>אין לומדות מוקצות עבורך</EmptyText>
            </EmptyState>
          ) : (
            <CourseGrid>
              {managerCourses.map((course) => (
                <CourseCard key={course.courseId._id} status={course.status}>
                  <StatusBadge
                    status={course.status}
                    style={{ marginBottom: '12px' }}
                  >
                    {getStatusText(course.status)}
                  </StatusBadge>
                  <CourseTitle>{course.courseId.title}</CourseTitle>
                  <CourseDescription>
                    {course.courseId.description}
                  </CourseDescription>

                  <ProgressBar>
                    <ProgressFill width={course.progress} />
                  </ProgressBar>
                  <ProgressText>
                    <span>{course.progress}% הושלם</span>
                    {course.score != null && <span>ציון: {course.score}%</span>}
                  </ProgressText>

                  <CourseMeta>
                    {course.courseId.estimatedDuration && (
                      <MetaItem>
                        ⏱️ {course.courseId.estimatedDuration} דק'
                      </MetaItem>
                    )}
                  </CourseMeta>

                  {course.status === 'pending' && (
                    <Button
                      variant='primary'
                      block
                      onClick={() => navigate(`/${course.courseId.slug}`)}
                    >
                      התחל לומדה
                    </Button>
                  )}
                  {course.status === 'in_progress' && (
                    <Button
                      variant='primary'
                      block
                      onClick={() => navigate(`/${course.courseId.slug}`)}
                    >
                      המשך לימוד
                    </Button>
                  )}
                  {course.status === 'completed' && (
                    <ActionButtons $columns='1fr 1fr'>
                      <Button
                        variant='success'
                        onClick={() =>
                          navigate(`/${course.courseId.slug}/results`)
                        }
                      >
                        תוצאות
                      </Button>
                      <Button
                        variant='secondary'
                        onClick={() => navigate(`/${course.courseId.slug}`)}
                      >
                        בצע שוב
                      </Button>
                    </ActionButtons>
                  )}
                </CourseCard>
              ))}
            </CourseGrid>
          )}
        </Section>
      )}

      {/* Tab: My Exams */}
      {activeTab === 'my-exams' && (
        <Section>
          <SectionTitle>📝 המבחנים שלי</SectionTitle>
          {managerExams.length === 0 ? (
            <EmptyState>
              <EmptyIcon>📭</EmptyIcon>
              <EmptyTitle>אין מבחנים</EmptyTitle>
              <EmptyText>אין מבחנים מוקצים עבורך</EmptyText>
            </EmptyState>
          ) : (
            <CourseGrid>
              {managerExams.map((exam) => (
                <CourseCard key={exam.examId._id} status={exam.status}>
                  <StatusBadge
                    status={exam.hasPassed ? 'completed' : exam.status}
                    style={{ marginBottom: '12px' }}
                  >
                    {exam.hasPassed
                      ? 'עבר'
                      : exam.status === 'failed'
                        ? 'נכשל'
                        : 'ממתין'}
                  </StatusBadge>
                  <CourseTitle>{exam.examId.title}</CourseTitle>
                  <CourseDescription>
                    {exam.examId.description}
                  </CourseDescription>

                  <ProgressBar>
                    <ProgressFill
                      width={exam.progress}
                      danger={exam.status === 'failed'}
                    />
                  </ProgressBar>
                  <ProgressText>
                    <span>
                      ניסיונות: {exam.attempts}/{exam.examId.maxAttempts}
                    </span>
                    {exam.score != null && <span>ציון: {exam.score}%</span>}
                  </ProgressText>

                  <CourseMeta>
                    {exam.examId.timeLimit && (
                      <MetaItem>⏱️ {exam.examId.timeLimit} דק'</MetaItem>
                    )}
                    <MetaItem>🎯 עובר: {exam.examId.passScore}%</MetaItem>
                  </CourseMeta>

                  {exam.status === 'pending' && !exam.maxAttemptsReached && (
                    <Button
                      variant='primary'
                      block
                      onClick={() => navigate(`/exams/${exam.examId.slug}`)}
                    >
                      התחל מבחן
                    </Button>
                  )}
                  {exam.status === 'failed' && !exam.maxAttemptsReached && (
                    <Button
                      variant='warning'
                      block
                      onClick={() => navigate(`/exams/${exam.examId.slug}`)}
                    >
                      נסה שוב ({exam.examId.maxAttempts - exam.attempts} נותרו)
                    </Button>
                  )}
                  {exam.status === 'completed' && (
                    <Button
                      variant='success'
                      block
                      onClick={() =>
                        navigate(`/exams/${exam.examId.slug}/results`)
                      }
                    >
                      צפה בתוצאות
                    </Button>
                  )}
                </CourseCard>
              ))}
            </CourseGrid>
          )}
        </Section>
      )}

      {/* Employee Details Modal */}
      {selectedEmployee && (
        <ModalOverlay onClick={() => setSelectedEmployee(null)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalClose onClick={() => setSelectedEmployee(null)}>✕</ModalClose>

            {loadingDetails ? (
              <LoadingContainer
                style={{ minHeight: 'auto', padding: '60px 0' }}
              >
                <Spinner />
                <LoadingText>טוען פרטים...</LoadingText>
              </LoadingContainer>
            ) : employeeDetails ? (
              <>
                <ModalHeader>
                  <ModalAvatar>
                    {employeeDetails.fullName?.charAt(0)}
                  </ModalAvatar>
                  <div>
                    <ModalTitle>{employeeDetails.fullName}</ModalTitle>
                    <ModalSubtitle>
                      {employeeDetails.email} • {employeeDetails.sector}
                    </ModalSubtitle>
                  </div>
                </ModalHeader>

                <ModalStats>
                  <ModalStat>
                    <ModalStatValue>
                      {employeeDetails.stats?.totalCourses || 0}
                    </ModalStatValue>
                    <ModalStatLabel>קורסים</ModalStatLabel>
                  </ModalStat>
                  <ModalStat>
                    <ModalStatValue>
                      {employeeDetails.stats?.completedCourses || 0}
                    </ModalStatValue>
                    <ModalStatLabel>הושלמו</ModalStatLabel>
                  </ModalStat>
                  <ModalStat>
                    <ModalStatValue>
                      {employeeDetails.overallProgress || 0}%
                    </ModalStatValue>
                    <ModalStatLabel>התקדמות</ModalStatLabel>
                  </ModalStat>
                  <ModalStat>
                    <ModalStatValue>
                      {employeeDetails.stats?.passedExams || 0}
                    </ModalStatValue>
                    <ModalStatLabel>מבחנים עברו</ModalStatLabel>
                  </ModalStat>
                </ModalStats>

                <ModalSection>
                  <ModalSectionTitle>📚 קורסים</ModalSectionTitle>
                  <ModalCourseList>
                    {employeeDetails.courses?.map((course) => (
                      <ModalCourse key={course.courseSlug}>
                        <ModalCourseHeader>
                          <ModalCourseTitle>
                            {course.courseTitle || course.courseSlug}
                          </ModalCourseTitle>
                          <StatusBadge status={course.status}>
                            {getStatusText(course.status)}
                          </StatusBadge>
                        </ModalCourseHeader>
                        <ModalCourseProgress>
                          <ProgressBar style={{ flex: 1 }}>
                            <ProgressFill width={course.progress} />
                          </ProgressBar>
                          <span>{course.progress}%</span>
                          {course.score != null && (
                            <Score>ציון: {course.score}</Score>
                          )}
                        </ModalCourseProgress>
                      </ModalCourse>
                    ))}
                  </ModalCourseList>
                </ModalSection>

                {employeeDetails.exams?.length > 0 && (
                  <ModalSection>
                    <ModalSectionTitle>📝 מבחנים</ModalSectionTitle>
                    <ModalCourseList>
                      {employeeDetails.exams.map((exam) => (
                        <ModalCourse key={exam.examSlug}>
                          <ModalCourseHeader>
                            <ModalCourseTitle>
                              {exam.examTitle}
                            </ModalCourseTitle>
                            <StatusBadge
                              status={
                                exam.hasPassed
                                  ? 'completed'
                                  : exam.status === 'failed'
                                    ? 'failed'
                                    : 'pending'
                              }
                            >
                              {exam.hasPassed
                                ? 'עבר'
                                : exam.status === 'failed'
                                  ? 'נכשל'
                                  : 'טרם נבחן'}
                            </StatusBadge>
                          </ModalCourseHeader>
                          {exam.score != null && (
                            <div style={{ fontSize: '0.85rem', color: '#666' }}>
                              ציון: {exam.score}% | ניסיונות: {exam.attempts}
                            </div>
                          )}
                        </ModalCourse>
                      ))}
                    </ModalCourseList>
                  </ModalSection>
                )}
              </>
            ) : (
              <EmptyState>
                <EmptyText>לא נמצאו פרטים</EmptyText>
              </EmptyState>
            )}
          </Modal>
        </ModalOverlay>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <ModalOverlay onClick={() => setShowEmailModal(false)}>
          <Modal
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '500px' }}
          >
            <ModalClose onClick={() => setShowEmailModal(false)}>✕</ModalClose>

            <ModalTitle style={{ marginBottom: '24px' }}>
              📧 שליחת מייל ל-{selectedEmployees.length} עובדים
            </ModalTitle>

            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                }}
              >
                נושא
              </label>
              <input
                type='text'
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder='הכנס נושא...'
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                }}
              >
                הודעה
              </label>
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder='הכנס הודעה...'
                rows={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  resize: 'vertical',
                }}
              />
            </div>

            <ActionButtons columns='1fr 1fr'>
              <Button
                variant='secondary'
                onClick={() => setShowEmailModal(false)}
              >
                ביטול
              </Button>
              <Button variant='primary' onClick={handleSendEmail}>
                📧 שלח מייל
              </Button>
            </ActionButtons>
          </Modal>
        </ModalOverlay>
      )}
    </DashboardContainer>
  );
};

export default SectorManagerDashboard;
